import { getParamsFromNode, sendCustom } from '@linecorp/dosi-analytics';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { format } from 'date-fns';
import { Fragment, useEffect, useRef } from 'react';
import { useLogin } from '~/components/Auth';
import { BaseCard } from '~/components/Card';
import { Don } from '~/components/Don';
import { useCheckIn, useEvent } from '~/components/Event/CheckIn/CheckIn.hooks';
import { T } from '~/components/I18n';
import { DonIcon } from '~/components/Icon/Don';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { useMember } from '~/components/Member/Member.hooks';
import { useMembership } from '~/components/Membership/Membership.hooks';
import { Popup, PopupProps } from '~/components/Modal/Popup/Popup';
import { formatDon } from '~/components/Util/number';
import styles from './CheckInResultPopup.module.scss';

const resetTime = format(new Date('2023-01-01T00:00:00+00:00').getHours(), 'p');

export type CheckInResultPopupProps = PopupProps;

export const CheckInResultPopup = ({ onDismiss, ...props }: CheckInResultPopupProps) => {
  const queryClient = useQueryClient();
  const ref = useRef<HTMLDivElement>(null);
  const { data: checkInEvent, isFetched: isEventFetched } = useEvent('CHECK_IN');

  const { data, mutate, isLoading, isSuccess } = useCheckIn({
    onSuccess: () => {
      queryClient.invalidateQueries(['events']);
      queryClient.invalidateQueries(['balance']);
    },
  });
  const { maxLevel = 1, totalAmount = 0, reward = [], missionBuff } = data || {};

  const { amount: missionAmount, missionList = [] } = missionBuff || {};

  const {
    data: { isLoggedIn },
  } = useMember();

  const login = useLogin({ returnTargetURI: `${location.pathname}?modal=checkIn` });

  const {
    data: { citizenNftCount, additionalNftCount },
  } = useMembership();

  useEffect(() => {
    if (!isLoggedIn) {
      login();
      if (onDismiss) onDismiss();
      return;
    }
    if (!isEventFetched) return;
    if (!checkInEvent?.isJoinable && onDismiss) return onDismiss();

    mutate();
    // eslint-disable-next-line
  }, [isLoggedIn, isEventFetched]);

  useEffect(() => {
    if (!isSuccess) return;
    if (!ref.current) return;
    const params = getParamsFromNode(ref.current, 'event');
    sendCustom('check_in', { page_name: params.page_name });
  }, [additionalNftCount, citizenNftCount, isSuccess, maxLevel, totalAmount]);

  if (!isSuccess || isLoading) return null;

  return (
    <Popup
      {...props}
      title={<T name="citizen_checkin_complete_title" options={[formatDon(totalAmount)]} />}
      description={<T name="citizen_checkin_complete_desc3" pre="pre-line" options={[resetTime]} />}
      onDismiss={onDismiss}
      className={styles.root}>
      <BaseCard className={styles.body} shadow ref={ref}>
        <DescriptionList layout="horizontal" className={styles.receipt}>
          {reward.map(({ level, amount, holdingNums }) => (
            <Fragment key={level}>
              <dt>
                Lv. {level} (x{holdingNums})
              </dt>
              <dd className={classNames({ [styles.inactive]: amount === 0 })}>
                <Don value={amount} />
              </dd>
            </Fragment>
          ))}
          <dt>
            <T name="citizen_mission_status_reward" /> (x{missionList.length})
          </dt>
          <dd
            className={classNames({
              [styles.inactive]: missionList.length === 0,
            })}>
            <Don value={missionAmount} />
          </dd>
          <dd className={styles.divider} />
          <dt>
            <T name="citizen_checkin_complete_total" />
          </dt>
          <dd className={styles.total}>
            <DonIcon className={styles.icon} />
            <Don value={totalAmount} />
          </dd>
        </DescriptionList>
      </BaseCard>
    </Popup>
  );
};
